import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatEventTime } from '@/lib/formatEventTime';
import { EVENT_TIMEZONES, detectDefaultTimezone } from '@/lib/eventTimezones';
import { ArrowLeft, ArrowRight, Loader2, Calendar, MapPin, DollarSign, Check, X, Tag, Heart, AlertCircle } from 'lucide-react';
import { ImageUpload } from '@/components/ImageUpload';
import TimePicker12h from '@/components/TimePicker12h';
import DatePicker from '@/components/DatePicker';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const EVENT_TYPES = [
  { value: 'teaching', label: 'Teaching Session', desc: 'Instructional session for new or improving players', color: '#56c6df' },
  { value: 'tournament', label: 'Tournament', desc: 'Competitive play event', color: '#df5681' },
  { value: 'casual', label: 'Open Play', desc: 'Group play session', color: '#56dfb4' },
  { value: 'special', label: 'Special Event', desc: 'Themed events, charity games, club meetings', color: '#b456df' },
];
const LOCATION_TYPES = [
  { value: 'public', label: 'Public Venue' },
  { value: 'private', label: 'Private Residence' },
  { value: 'online', label: 'Online/Virtual' },
];
const SKILL_OPTIONS = ['newbie', 'beginner', 'intermediate', 'advanced', 'all'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const STEPS = ['Event Basics', 'Date & Time', 'Location', 'Details', 'Review & Pay'];

export default function PostEventPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingEventId, setPendingEventId] = useState(null);
  // After a checkout cancel, surface a persistent banner above the form
  // pointing the host to My Events to finish payment. Cleared when the
  // host dismisses it or starts a fresh checkout.
  const [cancelledDraftId, setCancelledDraftId] = useState(null);
  // Authoritative fee returned by /events/create-checkout — used in
  // the PayPal modal so what the user sees == what they're charged.
  const [pendingFee, setPendingFee] = useState(null);
  const [eventCoupon, setEventCoupon] = useState('');
  const [eventCouponLoading, setEventCouponLoading] = useState(false);
  const [eventCouponApplied, setEventCouponApplied] = useState(false);

  // localStorage breadcrumb so a host who closes the browser tab mid-PayPal
  // sees the recovery banner on their next visit to /events/post. Cleared on
  // successful capture; surfaced on mount and on banner dismiss.
  const PENDING_DRAFT_KEY = 'mfm_post_event_pending_draft_id';
  // Free-event affirmation modal state. Reset to false whenever the
  // host toggles `is_free` so they can't bypass the gate by clicking
  // Publish, dismissing, toggling paid, then toggling free again.
  const [freeConfirmOpen, setFreeConfirmOpen] = useState(false);
  const [freeConfirmAcknowledged, setFreeConfirmAcknowledged] = useState(false);
  // On mount: if a previous session left a pending-payment breadcrumb, hydrate
  // the cancelled-draft banner so the host doesn't lose track of their draft.
  useEffect(() => {
    try {
      const stashed = window.localStorage.getItem(PENDING_DRAFT_KEY);
      if (stashed) {
        setCancelledDraftId(stashed);
        // One-shot: clear so navigating back-and-forth doesn't keep re-showing.
        window.localStorage.removeItem(PENDING_DRAFT_KEY);
      }
    } catch { /* localStorage unavailable in private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warn before the tab unloads while a PayPal checkout is in flight so a
  // host doesn't accidentally lose track of their order_id by hitting
  // refresh / close. (Browsers ignore the custom string in modern Chrome
  // / Firefox but still show a generic confirm — that's enough.)
  useEffect(() => {
    if (!pendingOrderId) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [pendingOrderId]);
  const [form, setForm] = useState({
    title: '', event_type: 'casual', description: '',
    event_date: '', start_time: '', end_time: '', timezone: detectDefaultTimezone() || 'ET', is_recurring: false, recurring_frequency: '',
    venue_name: '', street_address: '', city: '', state: '', zip_code: '',
    is_destination_event: false, destination_label: '',
    location_type: 'public', is_free: true, cost_description: '', max_attendees: '',
    skill_levels: ['all'], contact_email: user?.email || '', external_link: '', additional_notes: '',
    signup_instructions: '',
    banner_image: '',
    // Charity event fields
    is_charity: false,
    charity_nonprofit_name: '',
    charity_website: '',
    charity_description: '',
    charity_flyer_url: '',
  });
  // Reset the free-event acknowledgment whenever the host toggles
  // is_free — prevents bypassing the gate by clicking Publish,
  // dismissing the modal, toggling paid, then toggling free again.
  useEffect(() => {
    setFreeConfirmAcknowledged(false);
  }, [form.is_free]);

  useEffect(() => {
    api.get('/paypal/config').then(({ data }) => setPaypalConfig(data)).catch(() => {});
  }, []);

  const isAdmin = user?.role === 'admin';
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleSkill = (s) => setForm(p => ({ ...p, skill_levels: p.skill_levels.includes(s) ? p.skill_levels.filter(x => x !== s) : [...p.skill_levels, s] }));

  // Phase 2: fee matrix mirrors backend `subscription_tiers.EVENT_POSTING_FEES`.
  // Tolerant of legacy tier values during migration window.
  const userTier = (user?.subscription_tier || '').toLowerCase();
  const isInstructor = !!user?.is_instructor;
  const isLegacyLifetime = !!user?.legacy_lifetime || userTier === 'lifetime';
  const effectiveTier = (() => {
    if (isLegacyLifetime) return 'instructor_unlimited';
    if (['open_play', 'verified_player', 'instructor_starter', 'instructor_plus', 'instructor_unlimited'].includes(userTier)) {
      return userTier;
    }
    // Legacy mapping
    if (userTier === 'monthly' || userTier === 'annual') {
      return isInstructor ? 'instructor_plus' : 'verified_player';
    }
    // free / empty / unknown
    return isInstructor ? 'instructor_starter' : 'open_play';
  })();
  const FEE_MATRIX = {
    open_play: { free: 0.99, paid: 9.99 },
    verified_player: { free: 0.99, paid: 9.99 },
    instructor_starter: { free: 0.99, paid: 9.99 },
    instructor_plus: { free: 0.00, paid: 6.99 },
    instructor_unlimited: { free: 0.00, paid: 0.00 },
  };
  const postingFeeBase = form.is_charity ? 0 : (form.is_free ? FEE_MATRIX[effectiveTier].free : FEE_MATRIX[effectiveTier].paid);
  // Summer Founding Partner override — mirrors backend
  // `subscription_tiers.is_summer_founding_partner_free`. Waives the
  // posting fee entirely for events on/before Labor Day 2026 (inclusive).
  const SUMMER_PARTNER_CUTOFF = '2026-09-07';
  const isSummerPartner = !!user?.is_summer_founding_partner;
  const summerPartnerApplies =
    isSummerPartner &&
    !!form.event_date &&
    String(form.event_date).slice(0, 10) <= SUMMER_PARTNER_CUTOFF;
  const postingFee = summerPartnerApplies ? 0 : postingFeeBase;
  const freePosts = user?.event_free_posts_remaining || 0;
  const isCompletelyFreeToPost = isAdmin || form.is_charity || summerPartnerApplies || freePosts > 0 || postingFee === 0;
  // Tiers where free-event posting fee is already $0. Used to label the
  // posting-fee summary on the Review step ("Free for paid members!").
  const isPaidMember = ['instructor_plus', 'instructor_unlimited'].includes(effectiveTier) || isLegacyLifetime;

  const validate = () => {
    if (step === 0) {
      if (!form.title) { toast.error('Please add an event title'); return false; }
      if (!form.event_type) { toast.error('Please pick an event type'); return false; }
      if (!form.description) { toast.error('Please add an event description'); return false; }
    }
    if (step === 1) {
      if (!form.event_date) { toast.error('Please pick the event date'); return false; }
      if (!form.start_time) { toast.error('Please pick a start time'); return false; }
      if (!form.timezone) { toast.error('Please pick a time zone (Eastern, Central, Pacific, etc.)'); return false; }
    }
    if (step === 2) {
      if (!form.venue_name) { toast.error('Please add the venue name'); return false; }
      if (!form.city) { toast.error('Please add the city'); return false; }
      if (form.is_destination_event) {
        // Destination events skip state + zip — they're often international
        // (cruises, foreign retreats) and don't fit US ZIP geography.
        if (!form.destination_label.trim()) {
          toast.error('Please add a short destination label (e.g., "Cancun, Mexico" or "Caribbean Cruise")');
          return false;
        }
      } else {
        if (!form.state) { toast.error('Please pick a state'); return false; }
        if (!form.zip_code) { toast.error('Please add a zip code'); return false; }
      }
    }
    if (step === 3) {
      if (form.is_charity) {
        if (!form.charity_nonprofit_name.trim()) { toast.error('Please name the nonprofit or charity benefiting from this event.'); return false; }
        if (!form.charity_website.trim()) { toast.error('Please add the nonprofit website.'); return false; }
        if (!form.charity_description.trim()) { toast.error('Please briefly describe how proceeds support the cause.'); return false; }
      }
    }
    return true;
  };
  const next = () => { if (validate()) setStep(s => Math.min(s + 1, 4)); };
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    // Guard: paid posts require PayPal config to render the modal. If the
    // user clicks Publish before /paypal/config has resolved, we'd silently
    // 404 the modal and show a "white screen". Block submit with clear
    // messaging instead.
    if (!isCompletelyFreeToPost && !paypalConfig) {
      toast.error('Payment options are still loading. Please wait a moment and try again.');
      return;
    }
    // Free-event verification gate — caught 24 Jun 2026 admin email:
    // "I'm seeing a lot of events listed as free when they're not
    // actually free." We require the host to actively confirm that
    // there's no attendee fee before publishing. Charity events are
    // exempt because they have their own affirmation flow already.
    if (form.is_free && !form.is_charity && !freeConfirmAcknowledged) {
      setFreeConfirmOpen(true);
      return;
    }
    setLoading(true);
    try {
      const eventData = { ...form, max_attendees: form.max_attendees ? parseInt(form.max_attendees) : null };
      const { data } = await api.post('/events/create-checkout', { event_data: eventData });
      if (data.published) {
        // Admin bypass or free-post-redeemed — event published directly
        toast.success('Event published!');
        navigate(`/events/${data.event_id}`);
        return;
      }
      if (data.order_id) {
        // Belt-and-suspenders: if paypalConfig somehow disappeared between
        // the guard above and now, point the host to My Events so they
        // can resume payment instead of being stranded on a blank page.
        if (!paypalConfig) {
          toast.error('Your event is saved as a draft. Open "My Events" to finish payment.');
          navigate('/events/my-events');
          return;
        }
        // Starting a fresh checkout — clear any prior cancellation banner.
        setCancelledDraftId(null);
        // Stash a breadcrumb so a closed/refreshed tab still surfaces the
        // recovery banner on the host's next visit to /events/post.
        try { window.localStorage.setItem(PENDING_DRAFT_KEY, data.event_id); }
        catch { /* ignore */ }
        setPendingOrderId(data.order_id);
        setPendingEventId(data.event_id);
        // Trust the backend's fee for display in the PayPal modal.
        setPendingFee(typeof data.fee === 'number' ? data.fee : null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create event');
    } finally { setLoading(false); }
  };

  const handleCancelCheckout = () => {
    // Stash the draft id so the page can render a persistent recovery
    // banner pointing the host to My Events. Triggered by both the
    // modal's X button and PayPal's `onCancel` (window closed / dismissed).
    setCancelledDraftId(pendingEventId);
    // The banner now owns the recovery prompt — clear the localStorage
    // breadcrumb so a fresh visit doesn't redundantly re-trigger.
    try { window.localStorage.removeItem(PENDING_DRAFT_KEY); }
    catch { /* ignore */ }
    setPendingOrderId(null);
    setPendingEventId(null);
    setPendingFee(null);
  };

  const dismissCancellationBanner = () => setCancelledDraftId(null);

  const handleApplyEventCoupon = async () => {
    const code = eventCoupon.trim().toUpperCase();
    if (!code) return;
    setEventCouponLoading(true);
    try {
      const { data } = await api.post('/coupons/redeem-event', { code });
      toast.success(data.message || 'Coupon applied!');
      setEventCouponApplied(true);
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setEventCouponLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div className="space-y-4">
          <div>
            <Label className="text-sm mb-1.5 block">Event Title *</Label>
            <Input value={form.title} onChange={e => u('title', e.target.value)} placeholder="e.g. Beginner's Mahjong Night" className="rounded-xl" data-testid="event-title" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-3 block">Event Type *</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {EVENT_TYPES.map(t => (
                <button key={t.value} type="button" onClick={() => u('event_type', t.value)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${form.event_type === t.value ? 'shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                  style={form.event_type === t.value ? { borderColor: t.color, backgroundColor: `${t.color}08` } : {}}
                  data-testid={`event-type-${t.value}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                    <span className="font-medium text-sm">{t.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Description * <span className="text-slate-400">({form.description.length}/1000)</span></Label>
            <Textarea value={form.description} onChange={e => u('description', e.target.value.slice(0, 1000))} rows={4} placeholder="Tell people about your event..." className="rounded-xl" data-testid="event-description" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Event Banner Image (optional)</Label>
            <ImageUpload value={form.banner_image} onChange={v => u('banner_image', v)} label="Upload Banner" shape="rect" />
            <p className="text-xs text-slate-500 mt-2" data-testid="event-banner-size-hint">
              Best size: <strong>1600 × 600 px</strong> (wide banner, 16:6 ratio). Keep important text centered — grid thumbnails crop top &amp; bottom. JPG or PNG, max 10MB.
            </p>
          </div>
        </div>
      );
      case 1: return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm mb-1.5 block">Event Date *</Label>
              <DatePicker value={form.event_date} onChange={v => u('event_date', v)} testId="event-date" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Start Time *</Label>
              <TimePicker12h value={form.start_time} onChange={v => u('start_time', v)} testId="event-start-time" />
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">End Time (optional)</Label>
            <TimePicker12h value={form.end_time} onChange={v => u('end_time', v)} testId="event-end-time" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Time Zone *</Label>
            <select
              value={form.timezone}
              onChange={e => u('timezone', e.target.value)}
              className="w-full max-w-xs rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#56c6df]"
              data-testid="event-timezone"
            >
              {EVENT_TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">Times are entered in the venue's local time zone — pick the one your attendees will see.</p>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => u('is_recurring', !form.is_recurring)}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); u('is_recurring', !form.is_recurring); } }}
            className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-slate-300 transition-colors select-none"
          >
            <Switch
              checked={form.is_recurring}
              onCheckedChange={v => u('is_recurring', v)}
              onClick={(e) => e.stopPropagation()}
              data-testid="event-recurring"
            />
            <div><p className="font-medium text-sm">Recurring Event</p><p className="text-xs text-slate-500">Each occurrence requires a separate posting fee</p></div>
          </div>
          {form.is_recurring && (
            <div className="flex gap-2">
              {['weekly', 'biweekly', 'monthly'].map(f => (
                <button key={f} type="button" onClick={() => u('recurring_frequency', f)}
                  className={`px-4 py-2 rounded-full text-sm border capitalize transition-all ${form.recurring_frequency === f ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200'}`}>
                  {f}
                </button>
              ))}
            </div>
          )}
        </div>
      );
      case 2: return (
        <div className="space-y-4">
          {/* Destination event opt-in. When checked we bypass state+zip
              validation and surface the event to ALL users nationwide. */}
          <div className="p-4 rounded-2xl border border-[#56c6df]/30 bg-[#56c6df]/5" data-testid="event-destination-section">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.is_destination_event}
                onCheckedChange={(v) => u('is_destination_event', !!v)}
                data-testid="event-is-destination"
                className="mt-0.5"
              />
              <div className="flex-1">
                <p className="font-medium text-sm flex items-center gap-1.5">
                  🌍 This is a destination event (cruise, retreat, international, or fly-in).
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Destination events are visible to everyone nationwide regardless of their distance filter. Use this for Mahjong cruises, out-of-state retreats, or international getaways.
                </p>
              </div>
            </label>
            {form.is_destination_event && (
              <div className="mt-4 pl-8">
                <Label className="text-sm mb-1 block">Destination label *</Label>
                <Input
                  value={form.destination_label}
                  onChange={e => u('destination_label', e.target.value.slice(0, 120))}
                  placeholder='e.g. "Cancun, Mexico" or "Caribbean Cruise"'
                  className="rounded-xl"
                  data-testid="event-destination-label"
                  maxLength={120}
                />
                <p className="text-xs text-slate-400 mt-1">Short and recognizable — this is what appears on the event card in place of distance.</p>
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm mb-1.5 block">Venue Name *</Label>
            <Input value={form.venue_name} onChange={e => u('venue_name', e.target.value)} placeholder={form.is_destination_event ? "e.g. Royal Caribbean Symphony of the Seas" : "e.g. Springfield Community Center"} className="rounded-xl" data-testid="event-venue" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Street Address</Label>
            <Input value={form.street_address} onChange={e => u('street_address', e.target.value)} className="rounded-xl" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm mb-1.5 block">City *</Label>
              <Input value={form.city} onChange={e => u('city', e.target.value)} className="rounded-xl" data-testid="event-city" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">State {form.is_destination_event ? '' : '*'}</Label>
              <select value={form.state} onChange={e => u('state', e.target.value)} className="w-full h-10 border border-slate-200 rounded-xl px-3 text-sm" data-testid="event-state" disabled={form.is_destination_event}>
                <option value="">{form.is_destination_event ? '— N/A —' : 'Select'}</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Zip Code {form.is_destination_event ? '' : '*'}</Label>
              <Input value={form.zip_code} onChange={e => u('zip_code', e.target.value)} maxLength={5} className="rounded-xl" data-testid="event-zip" disabled={form.is_destination_event} placeholder={form.is_destination_event ? '—' : ''} />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Location Type</Label>
            <div className="flex flex-wrap gap-2">
              {LOCATION_TYPES.map(l => (
                <button key={l.value} type="button" onClick={() => u('location_type', l.value)}
                  className={`px-4 py-2 rounded-full text-sm border transition-all ${form.location_type === l.value ? 'bg-[#56dfb4] text-slate-900 border-[#56dfb4]' : 'border-slate-200'}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
      case 3: return (
        <div className="space-y-4">
          {/* Charity event opt-in. Fee gets waived when checked. */}
          <div className="p-4 rounded-2xl border border-[#df5681]/30 bg-[#df5681]/5" data-testid="event-charity-section">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.is_charity}
                onCheckedChange={(v) => u('is_charity', !!v)}
                data-testid="event-is-charity"
                className="mt-0.5"
              />
              <div className="flex-1">
                <p className="font-medium text-sm flex items-center gap-1.5">
                  <Heart className="w-4 h-4 text-[#df5681]" />
                  This event benefits a registered nonprofit or charitable cause.
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Meet For Mahjong is happy to waive posting fees for events supporting charitable causes and nonprofit organizations. Commercial instructor events do not qualify.
                </p>
              </div>
            </label>
            {form.is_charity && (
              <div className="mt-4 space-y-3 pl-8">
                <div>
                  <Label className="text-sm mb-1 block">Nonprofit / Charity Name *</Label>
                  <Input
                    value={form.charity_nonprofit_name}
                    onChange={e => u('charity_nonprofit_name', e.target.value.slice(0, 200))}
                    placeholder="e.g. American Red Cross"
                    className="rounded-xl"
                    data-testid="event-charity-name"
                    maxLength={200}
                  />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Charity Website *</Label>
                  <Input
                    value={form.charity_website}
                    onChange={e => u('charity_website', e.target.value.slice(0, 500))}
                    placeholder="https://..."
                    className="rounded-xl"
                    data-testid="event-charity-website"
                    maxLength={500}
                  />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">How do proceeds help? * ({form.charity_description.length}/600)</Label>
                  <Textarea
                    value={form.charity_description}
                    onChange={e => u('charity_description', e.target.value.slice(0, 600))}
                    placeholder="e.g. 100% of door donations go directly to the local food bank."
                    rows={3}
                    className="rounded-xl"
                    data-testid="event-charity-description"
                    maxLength={600}
                  />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Flyer or info link (optional)</Label>
                  <Input
                    value={form.charity_flyer_url}
                    onChange={e => u('charity_flyer_url', e.target.value.slice(0, 500))}
                    placeholder="https://..."
                    className="rounded-xl"
                    data-testid="event-charity-flyer"
                    maxLength={500}
                  />
                </div>
                <p className="text-xs text-[#df5681] bg-[#df5681]/10 rounded-lg p-2.5">
                  <strong>Posting fee waived.</strong> {user?.verified_charity_organizer
                    ? "Your account is a verified charity organizer — your event publishes immediately with the Charity Event badge."
                    : "Your event publishes immediately. Our team will quickly verify the cause and confirm the Charity Event badge."}
                </p>
              </div>
            )}
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => u('is_free', !form.is_free)}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); u('is_free', !form.is_free); } }}
            className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-slate-300 transition-colors select-none"
            data-testid="event-is-free-row"
          >
            <Switch
              checked={form.is_free}
              onCheckedChange={v => u('is_free', v)}
              onClick={(e) => e.stopPropagation()}
              data-testid="event-is-free"
            />
            <div>
              <p className="font-medium text-sm">This event is free for attendees</p>
              <p className="text-xs text-slate-500">
                {form.is_charity
                  ? 'Charity events — posting fee waived'
                  : (summerPartnerApplies
                      ? 'Summer Founding Partner — free through Labor Day 2026'
                      : `Posting fee: $${postingFee.toFixed(2)}`)}
              </p>
            </div>
          </div>
          {!form.is_free && (
            <div>
              <Label className="text-sm mb-1.5 block">Cost to Attend</Label>
              <Input value={form.cost_description} onChange={e => u('cost_description', e.target.value)} placeholder="e.g. $10 entry fee" className="rounded-xl" data-testid="event-cost" />
            </div>
          )}
          <div>
            <Label className="text-sm mb-1.5 block">Maximum Attendees (optional)</Label>
            <Input type="number" value={form.max_attendees} onChange={e => u('max_attendees', e.target.value)} placeholder="Leave blank for unlimited" className="rounded-xl max-w-xs" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Skill Levels Welcome</Label>
            <div className="flex flex-wrap gap-2">
              {SKILL_OPTIONS.map(s => (
                <button key={s} type="button" onClick={() => toggleSkill(s)}
                  className={`px-3 py-1.5 rounded-full text-sm border capitalize transition-all ${form.skill_levels.includes(s) ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200'}`}
                  data-testid={`event-skill-${s}`}>
                  {s === 'all' ? 'All Levels' : s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Contact Email</Label>
            <Input value={form.contact_email} onChange={e => u('contact_email', e.target.value)} className="rounded-xl" data-testid="event-contact-email" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">External Link (optional)</Label>
            <Input value={form.external_link} onChange={e => u('external_link', e.target.value)} placeholder="https://..." className="rounded-xl" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Additional Notes ({form.additional_notes.length}/500)</Label>
            <Textarea value={form.additional_notes} onChange={e => u('additional_notes', e.target.value.slice(0, 500))} rows={3} className="rounded-xl" />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">How should attendees sign up and pay? ({form.signup_instructions.length}/500)</Label>
            <Textarea value={form.signup_instructions} onChange={e => u('signup_instructions', e.target.value.slice(0, 500))} rows={3} placeholder="e.g. RSVP via email, pay at the door, register on Eventbrite..." className="rounded-xl" data-testid="event-signup-instructions" />
          </div>
        </div>
      );
      case 4: return (
        <div className="space-y-4">
          <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Review Your Event</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ['Title', form.title],
              ['Type', EVENT_TYPES.find(t => t.value === form.event_type)?.label],
              ['Date', form.event_date],
              ['Time', formatEventTime(form.start_time, form.end_time, form.timezone)],
              ['Venue', form.venue_name],
              ['Location', `${form.city}, ${form.state} ${form.zip_code}`],
              ['Location Type', form.location_type],
              ['Free Event', form.is_free ? 'Yes' : 'No'],
              ...(form.is_free ? [] : [['Cost', form.cost_description]]),
              ['Skill Levels', form.skill_levels.join(', ')],
              ['Contact', form.contact_email],
            ].map(([label, value]) => (
              <div key={label} className="p-3 rounded-xl bg-slate-50">
                <p className="text-xs text-slate-500">{label}</p>
                <p className="text-sm font-medium capitalize">{value || 'N/A'}</p>
              </div>
            ))}
          </div>
          {form.description && (
            <div className="p-3 rounded-xl bg-slate-50">
              <p className="text-xs text-slate-500">Description</p>
              <p className="text-sm text-slate-700 line-clamp-3">{form.description}</p>
            </div>
          )}
          <div className="p-5 rounded-2xl border-2 border-[#56dfb4] bg-[#56dfb4]/5 flex items-center justify-between">
            <div>
              <p className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Posting Fee</p>
              <p className="text-sm text-slate-500" data-testid="event-fee-summary-text">
                {form.is_charity
                  ? 'Charity event — posting fee waived'
                  : (summerPartnerApplies
                    ? 'Summer Founding Partner — posting free through Labor Day 2026 ☀️'
                    : (freePosts > 0
                      ? `You have ${freePosts} free post${freePosts === 1 ? '' : 's'} — this one is on us!`
                      : (form.is_free
                          ? (isPaidMember ? 'Free for paid members!' : 'Free event listing')
                          : 'Paid event listing')))}
              </p>
              {isSummerPartner && !summerPartnerApplies && form.event_date && (
                <p className="text-xs text-amber-600 mt-1" data-testid="event-summer-partner-after-cutoff-note">
                  Note: This event is after Labor Day 2026, so your Summer Founding Partner benefit doesn't apply — standard pricing is shown.
                </p>
              )}
            </div>
            <span className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isCompletelyFreeToPost ? 'FREE' : `$${postingFee.toFixed(2)}`}
            </span>
          </div>

          {/* Event Promo / Coupon Code */}
          {!isCompletelyFreeToPost && (
            <div className="p-5 rounded-2xl border border-dashed border-[#b456df]/30 bg-[#b456df]/5" data-testid="event-coupon-section">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-[#b456df]" />
                <h4 className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Have a Promo Code?</h4>
              </div>
              <div className="flex gap-2 items-stretch">
                <Input
                  value={eventCoupon}
                  onChange={e => setEventCoupon(e.target.value.toUpperCase())}
                  placeholder="Enter promo code"
                  className="rounded-xl uppercase"
                  disabled={eventCouponApplied || eventCouponLoading}
                  data-testid="event-coupon-input"
                />
                <Button
                  onClick={handleApplyEventCoupon}
                  disabled={eventCouponLoading || eventCouponApplied || !eventCoupon.trim()}
                  className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] px-6"
                  data-testid="event-coupon-apply-btn"
                >
                  {eventCouponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (eventCouponApplied ? 'Applied' : 'Apply')}
                </Button>
              </div>
              {eventCouponApplied && (
                <p className="text-xs text-green-700 mt-2">Coupon applied — your posting fee has been waived for this event.</p>
              )}
            </div>
          )}
        </div>
      );
      default: return null;
    }
  };

  return (
    <div data-testid="post-event-page" className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Post an Event</h1>
      <p className="text-slate-500 mb-6">Step {step + 1} of {STEPS.length}: {STEPS[step]}</p>

      <Progress value={((step + 1) / STEPS.length) * 100} className="h-2 rounded-full mb-8" />

      {/* Persistent SFP confidence banner — shown at every step of the
          wizard so the host never wonders mid-flow whether the discount
          will actually apply. Renders for any SFP host regardless of
          whether they've picked a date yet; the fee summary at the end
          adds the after-cutoff caveat if their chosen event_date is past
          Labor Day 2026. */}
      {isSummerPartner && (
        <div
          className="mb-6 rounded-2xl border-2 border-emerald-400/40 bg-emerald-50 p-4 flex items-start gap-3"
          data-testid="sfp-fee-waived-banner"
        >
          <span className="text-2xl leading-none mt-0.5" aria-hidden>☀️</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-emerald-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Summer Founding Partner — posting fee waived
            </p>
            <p className="text-sm text-emerald-800 mt-0.5">
              You won&rsquo;t be charged a posting fee for any event held on or before <strong>Labor Day 2026 (Sept 7)</strong>. Thanks for being one of our launch hosts!
            </p>
          </div>
        </div>
      )}

      {cancelledDraftId && (
        <div
          className="mb-6 rounded-2xl border-2 border-[#df5681]/40 bg-[#df5681]/5 p-5"
          role="alert"
          data-testid="checkout-cancelled-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#df5681]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertCircle className="w-5 h-5 text-[#df5681]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment not completed
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Your event has <strong>not been published yet</strong>. We saved it as a draft so you don't lose your work. Visit <strong>My Events</strong> to finish payment and publish your event.
              </p>
              <div className="flex gap-2 mt-4 flex-wrap">
                <Link to="/events/my-events">
                  <Button
                    className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-1"
                    data-testid="cancelled-banner-go-to-my-events-btn"
                  >
                    Go to My Events <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={dismissCancellationBanner}
                  className="rounded-full"
                  data-testid="cancelled-banner-dismiss-btn"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Card className="border-slate-100">
        <CardContent className="p-8">
          {renderStep()}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
            {step > 0 ? (
              <Button variant="ghost" onClick={prev} className="rounded-full gap-1"><ArrowLeft className="w-4 h-4" /> Back</Button>
            ) : <div />}
            {step < 4 ? (
              <Button onClick={next} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-1 px-6" data-testid="event-next-btn">
                Next <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || (!isCompletelyFreeToPost && !paypalConfig)}
                className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] px-8 gap-2"
                data-testid="event-pay-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
                {isAdmin
                  ? 'Publish Free (Admin)'
                  : (freePosts > 0
                      ? 'Publish Free (Coupon)'
                      : (postingFee === 0
                          ? 'Publish Free (Paid Member)'
                          : (!paypalConfig
                              ? 'Loading payment options…'
                              : `Pay $${postingFee.toFixed(2)} & Publish`)))}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Free-event affirmation modal — host must actively confirm
          there's no attendee fee before publishing. Caught 24 Jun 2026
          admin email: "I'm seeing a lot of events listed as free when
          they're not actually free." */}
      {freeConfirmOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-4"
          data-testid="event-free-confirm-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            data-testid="event-free-confirm-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <DollarSign className="w-5 h-5 text-[#56dfb4]" />
                Confirm: Free for attendees?
              </h3>
              <button
                onClick={() => setFreeConfirmOpen(false)}
                className="p-1 hover:bg-slate-100 rounded-full"
                aria-label="Close"
                data-testid="event-free-confirm-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              You marked this event as <strong>free</strong>. That should mean attendees pay <strong>nothing at all</strong> — no door fee, no card fee, no &quot;buy-in&quot;, no required tip, no minimum food / drink purchase, no venue cover charge.
            </p>
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 text-xs text-amber-900 leading-relaxed">
              <strong>If there is ANY cost</strong> — even $5 for tea, even a venue minimum, even a tip jar — please go back and switch this event to <strong>paid</strong>, then describe the cost so attendees aren&apos;t surprised at the door.
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setFreeConfirmOpen(false)}
                className="flex-1 rounded-full"
                data-testid="event-free-confirm-go-back-btn"
              >
                ← Go back &amp; edit
              </Button>
              <Button
                onClick={() => {
                  setFreeConfirmAcknowledged(true);
                  setFreeConfirmOpen(false);
                  // Tail-call the submit now that the gate is cleared.
                  // Use a microtask so the state batch settles first.
                  setTimeout(() => handleSubmit(), 0);
                }}
                className="flex-1 rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]"
                data-testid="event-free-confirm-yes-btn"
              >
                Yes, it&apos;s truly free
              </Button>
            </div>
          </div>
        </div>
      )}

      {pendingOrderId && paypalConfig && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="event-paypal-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Complete Payment</h3>
              <button onClick={handleCancelCheckout} className="p-1 hover:bg-slate-100 rounded-full" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Posting fee: <strong>${(pendingFee ?? postingFee).toFixed(2)}</strong></p>
            <PayPalScriptProvider options={{ clientId: paypalConfig.client_id, currency: 'USD', intent: 'capture' }}>
              <PayPalButtons
                style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                forceReRender={[pendingOrderId]}
                createOrder={() => pendingOrderId}
                onApprove={async (data) => {
                  try {
                    await api.post(`/events/orders/${data.orderID}/capture`);
                    // Payment succeeded — clear the pending-draft breadcrumb
                    // so the recovery banner doesn't fire on next visit.
                    try { window.localStorage.removeItem(PENDING_DRAFT_KEY); }
                    catch { /* ignore */ }
                    toast.success('Event published!');
                    navigate(`/events/${pendingEventId}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Payment capture failed');
                  }
                }}
                onError={(err) => {
                  console.error('PayPal error:', err);
                  toast.error('PayPal error. Please try again.');
                }}
                onCancel={handleCancelCheckout}
              />
            </PayPalScriptProvider>
          </div>
        </div>
      )}
    </div>
  );
}
